/**
 * ================================================================================================
 *  WHAT THE BILLING RUN'S OUTCOME WORDS MEAN, IN HEBREW
 * ================================================================================================
 *
 * The run answers a CLOSED VOCABULARY — `RUN_OUTCOMES` in `apps/api/src/services/billing-run.ts`,
 * whose own comment says why it is closed: *"the run's whole output is a tally of these and a word
 * nobody can enumerate is a tally nobody can check."*
 *
 * ── A WORD THIS FILE DOES NOT KNOW IS RENDERED, NOT SWALLOWED ──────────────────────────────────
 *
 * `billingOutcomeHe` returns the UNFAMILIAR code itself when it has no sentence for it, with the
 * neutral tone. The alternative — a default of "לא ידוע" — would make a new outcome word
 * indistinguishable from one this product cannot explain, on a screen where the word is the whole
 * answer. `billingOutcomes.test.ts` holds this map against the server's vocabulary, so a word with
 * no sentence fails the build rather than reaching an operator.
 *
 * ── THE TONES ARE NOT DECORATION ───────────────────────────────────────────────────────────────
 *
 * 09 §9.5 A3: colour is never the only carrier of meaning, which is why every entry is a SENTENCE
 * first. The tone groups them the way an operator reads the table — what happened, what will
 * happen, what did not happen and why, and what needs somebody.
 */

/*
 * THE PRODUCT'S FOUR TONES AND NOT A FIFTH. `Tone` in `adminVocabulary.ts` is
 * `ok | warn | error | muted`, and `<StatusChip>` takes exactly that. A local `blocked` would
 * have been a fifth vocabulary for the same four colours -- which is how two screens come to
 * disagree about what red means.
 */
export type OutcomeTone = Tone;

import type { Tone } from './adminVocabulary';

export interface OutcomeText {
  readonly labelHe: string;
  readonly tone: OutcomeTone;
}

/**
 * KEYED BY THE SERVER'S CODE. The keys are not re-declared as a type here: this file must be able
 * to hold a word the server adds tomorrow, and a union would make that a compile error in the
 * browser rather than a failing control in the suite that is for exactly that.
 */
const OUTCOMES: Readonly<Record<string, OutcomeText>> = {
  /* ── IT HAPPENED ─────────────────────────────────────────────────────────────────────────── */
  charged: { labelHe: 'חויב', tone: 'ok' },
  already_charged: { labelHe: 'חויב כבר — לא נגבה פעמיים', tone: 'ok' },

  /* ── IT WOULD HAPPEN. The only outcome a press changes. ──────────────────────────────────── */
  would_charge: { labelHe: 'מוכן לחיוב', tone: 'warn' },

  /* ── IT DID NOT HAPPEN, AND THAT IS CORRECT ──────────────────────────────────────────────── */
  skipped_trial: { labelHe: 'בתקופת ניסיון — אין מה לגבות', tone: 'muted' },
  skipped_cancelled: { labelHe: 'המנוי בוטל — אין חידוש', tone: 'muted' },
  skipped_nothing_to_bill: { labelHe: 'התקופה עולה אפס', tone: 'muted' },
  skipped_not_due: { labelHe: 'לא מיועד לחיוב בתאריך הזה', tone: 'muted' },
  /*
   * 2026-11-03. The table now LISTS every customer for the period, so a subscription this product
   * does not bill appears with its reason instead of being absent. `muted`, because nothing is
   * wrong and nothing needs doing: it is a row to read, not a row to act on.
   */
  skipped_not_billable_state: { labelHe: 'המנוי אינו פעיל — אין חיוב', tone: 'muted' },

  /* ── IT DID NOT HAPPEN, AND SOMEBODY HAS TO DO SOMETHING ─────────────────────────────────── */
  declined: { labelHe: 'הכרטיס סורב', tone: 'error' },
  /*
   * THE ONE OUTCOME THAT IS NOT A FACT ABOUT THE MONEY. The call did not complete, the key is
   * spent, and nobody may charge this period again until a person reads the key back from the
   * provider. Saying "נכשל" here would claim the money did not move, which is precisely what is
   * not known.
   */
  unknown: { labelHe: 'לא ידוע אם הכסף עבר — דורש בדיקה אצל הספק', tone: 'error' },
  blocked_no_instrument: { labelHe: 'אין כרטיס אשראי ללקוח', tone: 'error' },
  blocked_no_provider: { labelHe: 'אין ספק סליקה מוגדר באחוזה', tone: 'error' },
  blocked_no_account: { labelHe: 'אין חשבון חיוב — אין למי להפיק מסמך', tone: 'error' },
  blocked_unpriced: { labelHe: 'לא ניתן לתמחר', tone: 'error' },
  blocked_no_vat_rate: { labelHe: 'אין שיעור מע״מ רשום לתקופה', tone: 'error' },
  blocked_missing_days: { labelHe: 'ימים בתקופה לא נמדדו', tone: 'error' },
  blocked_not_recorded: { labelHe: 'לא נרשם מסמך', tone: 'error' },
  blocked_in_flight: { labelHe: 'התקופה תפוסה — ניסיון אחר פתוח', tone: 'error' },
};

export function billingOutcomeHe(code: string): OutcomeText {
  /*
   * THE CODE ITSELF WHEN THERE IS NO SENTENCE. See the header: a default of "לא ידוע" would make
   * an unexplained word look like an explained one, and `unknown` above is a REAL outcome with a
   * meaning of its own — the two must never collide.
   */
  return OUTCOMES[code] ?? { labelHe: code, tone: 'muted' };
}

/** Every code this file has a sentence for. The control compares it to the server's vocabulary. */
export const DESCRIBED_BILLING_OUTCOMES: readonly string[] = Object.keys(OUTCOMES).sort();

/**
 * THE DUNNING STAGE, IN WORDS. `DunningStage` in `apps/api/src/domain/dunning.ts`.
 *
 * `current` WITH NO POLICY IN FORCE IS NOT THE SAME SENTENCE, and the caller passes `governed` so
 * the two can be told apart: 0011 gives the policy no default day counts, and an estate with no
 * policy row would otherwise report every customer as being in good standing.
 */
export function paymentStageHe(
  stage: 'current' | 'warning' | 'restricted' | 'terminated',
  governed: boolean,
): OutcomeText {
  if (!governed && stage === 'current') {
    return { labelHe: 'אין כשל פתוח. מדיניות גבייה לא הוגדרה', tone: 'muted' };
  }
  if (!governed) {
    return { labelHe: 'יש כשל פתוח, ומדיניות גבייה לא הוגדרה', tone: 'warn' };
  }
  switch (stage) {
    case 'current':
      return { labelHe: 'תקין', tone: 'ok' };
    case 'warning':
      return { labelHe: 'בהתראה — העבודה נמשכת כרגיל', tone: 'warn' };
    case 'restricted':
      return { labelHe: 'מוגבל — הסדרה בלבד', tone: 'error' };
    case 'terminated':
      return { labelHe: 'הופסק — נדרשת הרשמה מחדש', tone: 'error' };
  }
}

/**
 * WHAT A DOCUMENT'S STATUS MEANS. `CK_billing_document_status` admits exactly these four.
 *
 *     *"הבחן בין „טרם בוצע חיוב”, „טרם הופקה חשבונית”, נתון חסר וכשל בטעינה."*
 *
 * `pending_issue` IS *"טרם הופקה חשבונית"* — the charge is recorded and the provider has not
 * produced a document. It is not an error and it is not nothing.
 */
export function documentStatusHe(status: string): OutcomeText {
  switch (status) {
    case 'issued':
      return { labelHe: 'הופקה', tone: 'ok' };
    case 'pending_issue':
      return { labelHe: 'טרם הופקה חשבונית', tone: 'warn' };
    case 'provider_failed':
      return { labelHe: 'ההפקה נכשלה אצל הספק', tone: 'error' };
    case 'void':
      return { labelHe: 'בוטלה', tone: 'muted' };
    default:
      return { labelHe: status, tone: 'muted' };
  }
}
